import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { getUserAccessInfo, type AccessLevel } from "@/services/userAccessService";
import { accessLevelForUnknownStatus } from "@/services/accessGating";
import { logEvent } from "@/services/eventLogService";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SUBSCRIPTION_CHECKOUT_URL } from "@/config/subscription";
import { Capacitor } from "@capacitor/core";
import { useIapEnabled } from "@/hooks/useIapEnabled";
import IapPaywall from "@/components/subscription/IapPaywall";
import { ProgressLink } from "@/components/metrics/history/ProgressLink";
import { PROGRESS_PATH } from "@/lib/metricHistoryRoutes";

interface SubscriptionGateProps {
  children: React.ReactNode;
  /** Allow access during grace period (default true) */
  allowGrace?: boolean;
  /** Custom fallback instead of default subscribe screen */
  fallback?: React.ReactNode;
}

/**
 * Wraps a page/section and shows a "subscribe to access" screen when
 * the user's access level is insufficient (course_only or none).
 */
export function SubscriptionGate({
  children,
  allowGrace = true,
  fallback,
}: SubscriptionGateProps) {
  // Single source of truth for access — including impersonation-awareness.
  // (Previously this component duplicated useAccessLevel's logic, and the two
  // copies drifted: the admin-full shortcut here ignored impersonation, so
  // "view as user" showed everything unlocked regardless of the target.)
  const { accessLevel, loading } = useAccessLevel();
  const { enabled: iapEnabled, loading: iapLoading } = useIapEnabled();
  const isNative = Capacitor.isNativePlatform();

  // Optimistic unlock. After a store purchase the entitlement is real immediately,
  // but our own status takes several hops to catch up (store → RevenueCat → our
  // backend → the app). Making someone who has just been charged sit in front of a
  // paywall is the worst moment in the flow, so the store's own confirmation opens
  // the content right away; the server catches up behind it.
  const [justPurchased, setJustPurchased] = useState(false);

  // A lapsed member meets this screen BEFORE any tracker, so her own metric
  // history is otherwise unreachable from here. The link below is presentation
  // only — it decides nothing about who is gated, and `/progress` is deliberately
  // outside this gate (decision D2: one historical row = access, subscription or
  // not).
  const navigate = useNavigate();

  if (loading) return null;
  if (justPurchased) return <>{children}</>;

  // 'paused' (save-offer pause) keeps community surfaces (home, feed, chat) open — only
  // course/lesson content is blocked, via PausedCourseWall. Without this, paused
  // users would hit the generic subscribe screen and be pushed to buy a second
  // subscription while their pause has a scheduled auto-resume.
  const allowed =
    accessLevel === "full" ||
    accessLevel === "paused" ||
    (allowGrace && accessLevel === "grace");

  if (allowed) return <>{children}</>;

  if (fallback) return <>{fallback}</>;

  // Still asking app_config whether purchases are live. Render nothing rather than
  // the "not available" screen below: that screen tells a member they cannot buy,
  // and showing it for the length of a network round trip — to someone who just
  // tapped a banner offering them the subscription — is both wrong and the most
  // expensive possible moment to be wrong.
  if (isNative && iapLoading) return null;

  // Native + purchases live: buy in-app. Sending a native user to an external
  // checkout is an App Store 3.1.1 (anti-steering) rejection.
  if (isNative && iapEnabled) {
    return <IapPaywall onPurchased={() => setJustPurchased(true)} />;
  }

  // Native + purchases unavailable (kill-switch off, or products pulled from sale).
  // Deliberately NO external purchase link: an in-app route to web checkout is the
  // exact pattern rule 3.1.1 forbids, and a reviewer finding it fails the build.
  if (isNative) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center" dir="rtl">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
          <Lock className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">
          תוכן זה זמין למנויים בלבד
        </h2>
        <p className="text-muted-foreground max-w-sm">
          המנוי אינו זמין לרכישה כרגע. אפשר לנסות שוב מאוחר יותר, או לפנות אלינו לכל שאלה.
        </p>
        <ProgressLink
          className="mt-6"
          onOpen={() => navigate(PROGRESS_PATH)}
        />
      </div>
    );
  }

  // Web keeps the existing checkout — the App Store rules do not apply here.
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center" dir="rtl">
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
        <Lock className="w-8 h-8 text-primary" />
      </div>
      <h2 className="text-xl font-bold text-foreground mb-2">
        תוכן זה זמין למנויים בלבד
      </h2>
      <p className="text-muted-foreground max-w-sm">
        הירשמו למנוי כדי לקבל גישה מלאה לכל תכני האפליקציה,
        אתגרים קהילתיים, צ'אט, מעקב ועוד.
      </p>
      <Button
        size="lg"
        className="mt-6"
        onClick={() =>
          window.open(SUBSCRIPTION_CHECKOUT_URL, "_blank", "noopener,noreferrer")
        }
      >
        הצטרפות למנוי
      </Button>
      <ProgressLink
        className="mt-4"
        onOpen={() => navigate(PROGRESS_PATH)}
      />
    </div>
  );
}

/**
 * Hook to get the current user access level for conditional rendering.
 */
export function useAccessLevel(): { accessLevel: AccessLevel | null; loading: boolean } {
  const { user, isLoading, isAuthenticated } = useAuth();
  const { isImpersonating, impersonatedUser, effectiveUserId } = useImpersonation();
  const [accessLevel, setAccessLevel] = useState<AccessLevel | null>(null);
  const [loading, setLoading] = useState(true);

  // During impersonation ("view as user"), compute the VIEWED-AS user's access
  // from their subscription status + enrollments — not the admin's. Otherwise
  // the admin-full shortcut below fires on the admin's own role and every
  // impersonated user looks fully unlocked.
  const impSubStatus = impersonatedUser?.subscriptionStatus;

  useEffect(() => {
    // Admins see everything — but NOT while impersonating (then we want the
    // target's real, possibly-locked, experience).
    if (!isImpersonating && user?.userType === "admin") {
      setAccessLevel("full");
      setLoading(false);
      return;
    }

    // Auth is still booting — don't resolve a (possibly wrong) gated level from
    // a not-yet-loaded status. Keep the gate in its loading state.
    if (!isImpersonating && isLoading) {
      setLoading(true);
      return;
    }

    // Fail OPEN when the subscription status is UNKNOWN (boot RPC failed / no
    // profile loaded). Never wall a possibly-paying member on a failed read.
    const statusKnown = isImpersonating
      ? true
      : user?.subscriptionStatusKnown ?? false;
    const openLevel = accessLevelForUnknownStatus({
      isImpersonating,
      isAuthenticated,
      statusKnown,
    });
    if (openLevel) {
      setAccessLevel(openLevel);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const check = async () => {
      setLoading(true);
      try {
        const status = isImpersonating ? impSubStatus : user?.subscriptionStatus;
        const overrideUserId = isImpersonating ? effectiveUserId : undefined;
        const info = await getUserAccessInfo(status, overrideUserId);
        if (!cancelled) setAccessLevel(info.level);
      } catch (err) {
        // The access computation itself failed (network). Fail OPEN for a real
        // authenticated member (same rationale as accessLevelForUnknownStatus);
        // stay strict during impersonation. Log it — this catch was previously a
        // SILENT lockout with no telemetry.
        logEvent("warning", "auth", "access_level_load_failed", {
          error: err instanceof Error ? err.message : String(err),
          impersonating: isImpersonating,
        });
        if (!cancelled) setAccessLevel(isImpersonating ? "none" : "full");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [user?.subscriptionStatus, user?.subscriptionStatusKnown, user?.id, user?.userType, isImpersonating, impSubStatus, effectiveUserId, isLoading, isAuthenticated]);

  return { accessLevel, loading };
}
